/**
 * Plant Loop Editor Component
 *
 * Main visual editor for plant loops using React Flow.
 * Features a dual-panel layout for supply and demand sides.
 */

import { useCallback, useRef, useMemo, useState } from 'react';
import { useIsMobile } from '../hooks/useMediaQuery';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  useReactFlow,
  type OnConnect,
  type OnNodesChange,
  type OnEdgesChange,
  type OnSelectionChangeFunc,
  type NodeTypes,
  applyNodeChanges,
  applyEdgeChanges,
  type ReactFlowInstance,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { usePlantLoopStore } from './plantStore';
import { PlantNode } from './PlantNode';
import { PlantComponentPalette } from './PlantComponentPalette';
import { getPlantComponentDefinition } from './components';
import type { PlantNodeData, PlantLoopSide, PlantLoopType } from './types';
import { LOOP_COLORS, LOOP_TYPE_NAMES } from './types';

/**
 * Custom node types for React Flow
 */
const nodeTypes: NodeTypes = {
  plantNode: PlantNode,
};

/**
 * Get minimap node color based on component type
 */
function getMiniMapNodeColor(node: { data?: PlantNodeData }): string {
  if (!node.data?.componentType) return '#E0E0E0';
  const def = getPlantComponentDefinition(node.data.componentType);
  return def?.color || '#E0E0E0';
}

/**
 * Get edge color based on loop type and side
 */
function getEdgeColor(loopType?: PlantLoopType, side?: PlantLoopSide): string {
  if (!loopType) return '#78909C';
  const colors = LOOP_COLORS[loopType];
  return side === 'supply' ? colors.primary : colors.secondary;
}

interface PlantLoopEditorProps {
  /** Height of the editor */
  height?: string | number;
}

/**
 * Mobile slide-out drawer for component palette
 */
function MobilePaletteDrawer({
  isOpen,
  onClose,
  loopType,
  activeSide,
}: {
  isOpen: boolean;
  onClose: () => void;
  loopType?: PlantLoopType;
  activeSide: PlantLoopSide;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[85vw] max-w-[280px] bg-card shadow-xl flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="font-semibold text-foreground">Components</h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loopType && (
            <PlantComponentPalette
              loopType={loopType}
              activeSide={activeSide}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Supply/Demand side panel header
 */
function SidePanelHeader({
  side,
  loopType,
  nodeCount,
  isActive,
  onClick,
}: {
  side: PlantLoopSide;
  loopType?: PlantLoopType;
  nodeCount: number;
  isActive: boolean;
  onClick: () => void;
}) {
  const colors = loopType ? LOOP_COLORS[loopType] : { primary: '#78909C', secondary: '#B0BEC5' };
  const bgColor = side === 'supply' ? colors.primary : colors.secondary;

  return (
    <button
      onClick={onClick}
      className={`
        flex w-full items-center justify-between px-4 py-2 text-left transition-all
        ${isActive ? 'ring-2 ring-inset ring-blue-400' : ''}
      `}
      style={{ backgroundColor: bgColor }}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-white">
          {side === 'supply' ? 'SUPPLY SIDE' : 'DEMAND SIDE'}
        </span>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs text-white">
          {nodeCount} components
        </span>
      </div>
      {isActive && (
        <span className="rounded bg-white/30 px-2 py-0.5 text-xs text-white">
          Active
        </span>
      )}
    </button>
  );
}

/**
 * Plant Loop Editor inner component
 */
function PlantLoopEditorInner({ height = '100%' }: PlantLoopEditorProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const isMobile = useIsMobile();

  // Active side state
  const [activeSide, setActiveSide] = useState<PlantLoopSide>('supply');
  const [showPaletteDrawer, setShowPaletteDrawer] = useState(false);

  // Store state and actions
  const nodes = usePlantLoopStore((state) => state.nodes);
  const edges = usePlantLoopStore((state) => state.edges);
  const setNodes = usePlantLoopStore((state) => state.setNodes);
  const setEdges = usePlantLoopStore((state) => state.setEdges);
  const addNode = usePlantLoopStore((state) => state.addNode);
  const addEdge = usePlantLoopStore((state) => state.addEdge);
  const validateConnection = usePlantLoopStore((state) => state.validateConnection);
  const selectNodes = usePlantLoopStore((state) => state.selectNodes);
  const selectEdges = usePlantLoopStore((state) => state.selectEdges);
  const deleteSelected = usePlantLoopStore((state) => state.deleteSelected);
  const undo = usePlantLoopStore((state) => state.undo);
  const redo = usePlantLoopStore((state) => state.redo);
  const canUndo = usePlantLoopStore((state) => state.canUndo);
  const canRedo = usePlantLoopStore((state) => state.canRedo);
  const selectedNodeIds = usePlantLoopStore((state) => state.selectedNodeIds);
  const selectedEdgeIds = usePlantLoopStore((state) => state.selectedEdgeIds);
  const isDirty = usePlantLoopStore((state) => state.isDirty);
  const getActiveLoop = usePlantLoopStore((state) => state.getActiveLoop);
  const getNodesBySide = usePlantLoopStore((state) => state.getNodesBySide);

  const activeLoop = getActiveLoop();

  const { screenToFlowPosition } = useReactFlow();

  // Get nodes by side
  const supplyNodes = useMemo(() => getNodesBySide('supply'), [nodes, getNodesBySide]);
  const demandNodes = useMemo(() => getNodesBySide('demand'), [nodes, getNodesBySide]);

  /**
   * Handle node changes
   */
  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setNodes(applyNodeChanges(changes, nodes));
    },
    [nodes, setNodes]
  );

  /**
   * Handle edge changes
   */
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      setEdges(applyEdgeChanges(changes, edges));
    },
    [edges, setEdges]
  );

  /**
   * Handle new connection
   */
  const onConnect: OnConnect = useCallback(
    (connection) => {
      // Determine loop side from source node
      const sourceNode = nodes.find((n) => n.id === connection.source);
      const side = sourceNode?.data.loopSide || activeSide;
      addEdge(connection, side);
    },
    [addEdge, nodes, activeSide]
  );

  /**
   * Validate connection
   */
  const isValidConnection = useCallback(
    (connection: { source: string | null; target: string | null; sourceHandle: string | null; targetHandle: string | null }) => {
      const result = validateConnection(connection);
      return result.valid;
    },
    [validateConnection]
  );

  /**
   * Handle selection change
   */
  const onSelectionChange: OnSelectionChangeFunc = useCallback(
    ({ nodes: selectedNodes, edges: selectedEdgesNew }) => {
      selectNodes(selectedNodes.map((n) => n.id));
      selectEdges(selectedEdgesNew.map((e) => e.id));
    },
    [selectNodes, selectEdges]
  );

  /**
   * Handle drag over
   */
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * Handle drop to create new node
   */
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const componentType = event.dataTransfer.getData('application/plantflow');
      const loopSideData = event.dataTransfer.getData('loopSide');
      const dropSide: PlantLoopSide = (loopSideData === 'supply' || loopSideData === 'demand') ? loopSideData : activeSide;

      if (!componentType) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNode(componentType, position, dropSide);
    },
    [screenToFlowPosition, addNode, activeSide]
  );

  /**
   * Handle React Flow initialization
   */
  const onInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance;
  }, []);

  /**
   * Edge styling with loop type colors
   */
  const styledEdges = useMemo(() => {
    return edges.map((edge) => ({
      ...edge,
      style: {
        ...edge.style,
        stroke: getEdgeColor(activeLoop?.config.type, edge.data?.loopSide),
        strokeWidth: selectedEdgeIds.includes(edge.id) ? 3 : 2,
      },
      animated: true,
    }));
  }, [edges, selectedEdgeIds, activeLoop]);

  /**
   * Node styling with selection state
   */
  const styledNodes = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      selected: selectedNodeIds.includes(node.id),
    }));
  }, [nodes, selectedNodeIds]);

  // Show message if no loop is active
  if (!activeLoop) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/30">
        <div className="text-center px-4">
          <svg className="mx-auto h-16 w-16 text-muted-foreground/30" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-4h2v2h-2v-2zm0-2h2V7h-2v7z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-foreground">No Plant Loop Selected</h3>
          <p className="mt-2 text-sm text-muted-foreground">Create a new loop or select an existing one to start editing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-background" style={{ height }}>
      {/* Component Palette (desktop only) */}
      {!isMobile && (
        <div className="w-56 flex-shrink-0 border-r border-border">
          <PlantComponentPalette
            loopType={activeLoop.config.type}
            activeSide={activeSide}
          />
        </div>
      )}

      {/* Mobile: Palette drawer */}
      {isMobile && (
        <MobilePaletteDrawer
          isOpen={showPaletteDrawer}
          onClose={() => setShowPaletteDrawer(false)}
          loopType={activeLoop.config.type}
          activeSide={activeSide}
        />
      )}

      {/* Main Editor Area */}
      <div className="flex flex-1 flex-col">
        {/* Loop Header */}
        <div
          className="flex items-center gap-2 md:gap-4 border-b border-border px-2 md:px-4 py-2"
          style={{ backgroundColor: `${LOOP_COLORS[activeLoop.config.type].primary}15` }}
        >
          <div
            className="h-4 w-4 rounded-full flex-shrink-0"
            style={{ backgroundColor: LOOP_COLORS[activeLoop.config.type].primary }}
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground truncate">{activeLoop.config.name}</div>
            <div className="text-xs text-muted-foreground">{LOOP_TYPE_NAMES[activeLoop.config.type]}</div>
          </div>
          {!isMobile && (
            <div className="text-xs text-muted-foreground flex-shrink-0">
              Exit Temp: {activeLoop.config.designExitTemp}°C | Delta T: {activeLoop.config.designDeltaT}°C
            </div>
          )}
        </div>

        {/* Supply/Demand Side Headers */}
        <div className="grid grid-cols-2 border-b border-border">
          <SidePanelHeader
            side="supply"
            loopType={activeLoop.config.type}
            nodeCount={supplyNodes.length}
            isActive={activeSide === 'supply'}
            onClick={() => { setActiveSide('supply'); }}
          />
          <SidePanelHeader
            side="demand"
            loopType={activeLoop.config.type}
            nodeCount={demandNodes.length}
            isActive={activeSide === 'demand'}
            onClick={() => { setActiveSide('demand'); }}
          />
        </div>

        {/* React Flow Canvas */}
        <div className="flex-1" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={styledNodes}
            edges={styledEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onSelectionChange={onSelectionChange}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onInit={onInit}
            isValidConnection={isValidConnection}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            deleteKeyCode={null}
            selectionKeyCode={null}
            multiSelectionKeyCode="Shift"
            panOnScroll
            selectionOnDrag
            panOnDrag={[1, 2]}
          >
            <Background gap={15} size={1} />
            <Controls />
            <MiniMap
              nodeColor={getMiniMapNodeColor}
              nodeStrokeWidth={3}
              zoomable
              pannable
            />

            {/* Mobile: Components button */}
            {isMobile && (
              <Panel position="top-left">
                <button
                  onClick={() => setShowPaletteDrawer(true)}
                  className="flex items-center gap-2 rounded-lg bg-card px-3 py-2 text-sm font-medium shadow-md border border-border text-foreground hover:bg-muted"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                  <span>Components</span>
                </button>
              </Panel>
            )}

            {/* Toolbar Panel */}
            <Panel position="top-right" className="flex gap-1 md:gap-2">
              <button
                onClick={undo}
                disabled={!canUndo()}
                className="rounded bg-card px-2 md:px-3 py-1.5 text-sm shadow border border-border hover:bg-muted disabled:opacity-50 text-foreground"
                title="Undo (Ctrl+Z)"
              >
                {isMobile ? '↶' : 'Undo'}
              </button>
              <button
                onClick={redo}
                disabled={!canRedo()}
                className="rounded bg-card px-2 md:px-3 py-1.5 text-sm shadow border border-border hover:bg-muted disabled:opacity-50 text-foreground"
                title="Redo (Ctrl+Y)"
              >
                {isMobile ? '↷' : 'Redo'}
              </button>
              <button
                onClick={deleteSelected}
                disabled={selectedNodeIds.length === 0 && selectedEdgeIds.length === 0}
                className="rounded bg-card px-2 md:px-3 py-1.5 text-sm shadow border border-border hover:bg-muted disabled:opacity-50 text-foreground"
                title="Delete (Del)"
              >
                {isMobile ? '✕' : 'Delete'}
              </button>
            </Panel>

            {/* Status Panel */}
            <Panel position="bottom-left" className="rounded bg-card/90 px-2 md:px-3 py-1.5 text-xs md:text-sm shadow border border-border">
              <span className="text-muted-foreground">
                {isMobile ? `S:${supplyNodes.length} D:${demandNodes.length}` : `Supply: ${supplyNodes.length} | Demand: ${demandNodes.length} | ${edges.length} connections`}
              </span>
              {isDirty && <span className="ml-2 text-amber-600 dark:text-amber-400">Modified</span>}
            </Panel>

            {/* Selection info */}
            {(selectedNodeIds.length > 0 || selectedEdgeIds.length > 0) && (
              <Panel position="bottom-right" className="rounded bg-card/90 px-3 py-1.5 text-sm shadow border border-border">
                <span className="text-muted-foreground">
                  {selectedNodeIds.length > 0 && `${String(selectedNodeIds.length)} node${selectedNodeIds.length > 1 ? 's' : ''}`}
                  {selectedNodeIds.length > 0 && selectedEdgeIds.length > 0 && ', '}
                  {selectedEdgeIds.length > 0 && `${String(selectedEdgeIds.length)} edge${selectedEdgeIds.length > 1 ? 's' : ''}`}
                  {' selected'}
                </span>
              </Panel>
            )}

            {/* Side indicator overlay (desktop only) */}
            {!isMobile && (
              <Panel position="top-left" className="rounded bg-card/90 px-3 py-1.5 text-sm shadow border border-border">
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{
                      backgroundColor: activeSide === 'supply'
                        ? LOOP_COLORS[activeLoop.config.type].primary
                        : LOOP_COLORS[activeLoop.config.type].secondary,
                    }}
                  />
                  <span className="text-muted-foreground">
                    Editing: {activeSide === 'supply' ? 'Supply' : 'Demand'} Side
                  </span>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>
      </div>
    </div>
  );
}

/**
 * Plant Loop Editor with ReactFlowProvider wrapper
 */
export function PlantLoopEditor(props: PlantLoopEditorProps) {
  return (
    <ReactFlowProvider>
      <PlantLoopEditorInner {...props} />
    </ReactFlowProvider>
  );
}
